import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { Dropdown, type MenuProps } from 'antd';
import cn from 'classnames';

import cls from './dropdown-select.module.css';

export interface DropdownSelectProps {
    items: MenuProps['items'];
    initialSelectedKey?: string | null;
    isDisabled?: boolean;
    onClick?: MenuProps['onClick'];
    placeholder?: string;
    'data-tour'?: string;
}

export const DropdownSelect = memo(
    ({
        items,
        initialSelectedKey,
        isDisabled = false,
        onClick,
        placeholder,
        'data-tour': dataTour,
    }: DropdownSelectProps) => {
        const { t } = useTranslation();
        const [selectedLabel, setSelectedLabel] =
            useState<React.ReactNode | null>(null);
        const [selectedIcon, setSelectedIcon] =
            useState<React.ReactNode | null>(null);
        const [selectedKey, setSelectedKey] = useState<string | null>(
            initialSelectedKey || null,
        );
        const [isOpen, setIsOpen] = useState(false);

        // Update selected label and icon when items or selectedKey changes
        useEffect(() => {
            if (!items || !selectedKey) {
                setSelectedLabel(null);
                setSelectedIcon(null);

                return;
            }

            const selectedItem = items.find(
                (item) => item?.key === selectedKey,
            );

            if (selectedItem && 'label' in selectedItem) {
                setSelectedLabel(selectedItem.label);
                setSelectedIcon(
                    'icon' in selectedItem ? selectedItem.icon || null : null,
                );
            } else {
                setSelectedLabel(null);
                setSelectedIcon(null);
            }
        }, [items, selectedKey]);

        // Handle menu item click
        const handleMenuClick: MenuProps['onClick'] = (info) => {
            setSelectedKey(info.key);
            onClick?.(info);
            setIsOpen(false);
        };

        return (
            <Dropdown
                autoAdjustOverflow
                disabled={isDisabled}
                getPopupContainer={(triggerNode) =>
                    triggerNode.parentElement || document.body
                }
                menu={{
                    items: items || [],
                    selectable: true,
                    selectedKeys: selectedKey ? [selectedKey] : [],
                    onClick: handleMenuClick,
                    className: cls.dropdownMenu,
                }}
                onOpenChange={setIsOpen}
                overlayStyle={{ maxHeight: '300px', overflow: 'auto' }}
                placement="bottomRight"
                trigger={['click']}
            >
                <button
                    aria-expanded={isOpen}
                    aria-haspopup="true"
                    className={cn(cls.dropdownButton, {
                        [cls.dropdownButtonDisabled]: isDisabled,
                    })}
                    data-tour={dataTour}
                    type="button"
                >
                    {selectedIcon && (
                        <span
                            aria-hidden="true"
                            className={cls.dropdownButtonIcon}
                        >
                            {selectedIcon}
                        </span>
                    )}
                    <span>
                        {selectedLabel || placeholder || t('form.selectOption')}
                    </span>
                    <span
                        aria-hidden="true"
                        className={cls.dropdownButtonIcon}
                    >
                        {isOpen ? <UpOutlined /> : <DownOutlined />}
                    </span>
                </button>
            </Dropdown>
        );
    },
);

DropdownSelect.displayName = 'DropdownSelect';
